import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    CContainer, CRow, CCol, CCard, CCardHeader, CCardBody, CButton,
    CBadge, CAlert, CFormCheck, CSpinner, CModal, CModalHeader, CModalTitle, CModalBody,
    CTooltip, CListGroup, CListGroupItem, CTable, CTableHead, CTableRow,
    CTableHeaderCell, CTableBody, CTableDataCell
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { ResponsiveBar } from '@nivo/bar';
import { cilArrowLeft, cilHome, cilLockLocked, cilVideo, cilMonitor, cilSpeech, cilExternalLink, cilWarning, cilCheckCircle, cilClock, cilOptions, cilInfo } from '@coreui/icons';
import DatePicker, { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import enUS from 'date-fns/locale/en-US';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import StoreAlertsWidget from '../../components/StoreAlertsWidget/StoreAlertsWidget';
import StoreShiftAnalyticsWidget from '../../components/StoreShiftAnalyticsWidget/StoreShiftAnalyticsWidget';

// Компоненты платформы
import Loading from '../../components/Loading/Loading';
import MultiCameraMonitor from '../../components/MultiCameraMonitor/MultiCameraMonitor';
import RoiEditor from '../../components/RoiEditor/RoiEditor';
import AnalyticsBlock from '../../components/AnalyticsBlock/AnalyticsBlock';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import VisitorAnalyticsBlock from '../../components/VisitorAnalyticsBlock/VisitorAnalyticsBlock';

// Аудио
import AudioSearchDashboard from '../../components/AudioSearchDashboard/AudioSearchDashboard';
import AudioDetailModal from '../../components/AudioDetailModal/AudioDetailModal';

// Утилиты
import { formatToCameraUTC, formatDuration } from '../../utils/cameraUtils';
import { useCameraAnalytics } from '../CameraDetailPage/hooks/useCameraAnalytics';

registerLocale('ru', ru);
registerLocale('en', enUS);

// ============================================================================
// ФУНКЦИЯ ДЛЯ РАЗВОРАЧИВАНИЯ СОВМЕЩЕННЫХ ПОСТОВ НА ОТДЕЛЬНЫЕ КАМЕРЫ (ДЛЯ МОНИТОРА)
// ============================================================================
const flattenCameras = (cams) => {
    const flatList = [];
    cams.forEach(cam => {
        const isCombined = cam.is_combined || cam.is_combined_camera || (cam.additional_cameras && cam.additional_cameras.length > 0);
        if (isCombined) {
            if (cam.parent) flatList.push({ id: cam.parent, name: `${cam.name} (Ракурс 1)`, timezone_offset: cam.timezone_offset });
            if (cam.additional_cameras) {
                cam.additional_cameras.forEach((addId, idx) => {
                    flatList.push({ id: addId, name: `${cam.name} (Ракурс ${idx + 2})`, timezone_offset: cam.timezone_offset });
                });
            }
            if (cam.secondary_camera) flatList.push({ id: cam.secondary_camera, name: `${cam.name} (Доп)`, timezone_offset: cam.timezone_offset });
        } else if (cam.is_duplicate && cam.parent) {
            flatList.push({ id: cam.parent, name: cam.name, timezone_offset: cam.timezone_offset });
        } else {
            flatList.push({ id: cam.id, name: cam.name, timezone_offset: cam.timezone_offset });
        }
    });

    const unique = [];
    const seen = new Set();
    flatList.forEach(c => {
        if (!seen.has(c.id)) {
            seen.add(c.id);
            unique.push(c);
        }
    });
    return unique;
};

// ============================================================================
// ВНУТРЕННИЙ КОМПОНЕНТ: ИНТЕРАКТИВНАЯ КАРТОЧКА ОДНОЙ КАМЕРЫ (READ-ONLY)
// ============================================================================
const StoreCameraCard = ({ camera, startDate, endDate, globalShowRoi, onOpenMonitor, onAnalyticsLoaded }) => {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('general');

    const [snapshots, setSnapshots] = useState({});
    const [sourceCameraNames, setSourceCameraNames] = useState({});
    const [polygons, setPolygons] = useState({});

    const minPercentage = 5;
    const {
        personnelGeneralMeta, chairMeta, phoneMeta, workFlowersMeta, noUniformMeta, fridgeMeta, receiptMeta,
        unattendedClientMeta, dynamicSummary
    } = useCameraAnalytics(analytics, camera, t, minPercentage);

    const getSourceIds = useCallback((cam) => {
        if (!cam) return [];
        let ids = [];
        const isCombined = cam.is_combined || cam.is_combined_camera || (cam.additional_cameras && cam.additional_cameras.length > 0);
        if (isCombined) {
            if (cam.parent) ids.push(cam.parent);
            if (cam.additional_cameras && Array.isArray(cam.additional_cameras)) {
                ids.push(...cam.additional_cameras);
            }
            if (cam.secondary_camera && !ids.includes(cam.secondary_camera)) {
                ids.push(cam.secondary_camera);
            }
        } else if (cam.is_duplicate && cam.parent) {
            ids.push(cam.parent);
        } else {
            ids.push(cam.id);
        }
        return [...new Set(ids.filter((id) => id != null))];
    }, []);

    const sourceIds = useMemo(() => getSourceIds(camera), [camera, getSourceIds]);

    const monitorCameras = useMemo(() => {
        return sourceIds.map((id, index) => ({
            id: id,
            name: sourceIds.length > 1 ? `${camera.name} (Ракурс ${index + 1})` : camera.name,
            timezone_offset: camera.timezone_offset
        }));
    }, [sourceIds, camera]);

    // Автовыбор вкладки при загрузке режима
    useEffect(() => {
        if (camera.operating_mode === 'fridge') setActiveView('fridge_empty');
        else if (camera.operating_mode === 'receipt') setActiveView('receipts');
        else setActiveView('general');
    }, [camera]);

    // Загрузка живых снапшотов
    useEffect(() => {
        let isMounted = true;
        const fetchAllSnapshots = async () => {
            const token = localStorage.getItem("token");
            sourceIds.forEach(async (id) => {
                const isCombined = camera.is_combined || camera.is_combined_camera || (camera.additional_cameras && camera.additional_cameras.length > 0);
                const param = isCombined ? `?source_id=${id}` : "";
                try {
                    const nameRes = await axios.get(`/api/cameras/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
                    if (isMounted) setSourceCameraNames(prev => ({ ...prev, [id]: nameRes.data.name }));

                    const res = await axios.get(`/api/cameras/${camera.id}/snapshot/${param}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: "blob"
                    });

                    if (isMounted) {
                        const newUrl = URL.createObjectURL(res.data);
                        setSnapshots(prev => {
                            const oldUrl = prev[id];
                            if (oldUrl) setTimeout(() => URL.revokeObjectURL(oldUrl), 500);
                            return { ...prev, [id]: newUrl };
                        });
                    }
                } catch (e) {
                    console.error(`Ошибка загрузки ракурса ${id}`, e);
                }
            });
        };
        fetchAllSnapshots();
        return () => { isMounted = false; };
    }, [camera, sourceIds]);

    const handleImageLoaded = (id, { naturalWidth, naturalHeight }) => {
        let rawRoi = [];
        if (camera.is_combined || camera.is_combined_camera || (camera.additional_cameras && camera.additional_cameras.length > 0)) {
            rawRoi = camera.roi_polygon?.[String(id)] || [];
        } else {
            rawRoi = Array.isArray(camera.roi_polygon) ? camera.roi_polygon : [];
        }

        const scaledRoi = rawRoi.map(p => ({
            x: p.x * naturalWidth,
            y: p.y * naturalHeight
        }));

        setPolygons(prev => ({ ...prev, [id]: { current: scaledRoi } }));
    };

    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token");
                const offset = camera.timezone_offset || 0;
                const reqStartIso = formatToCameraUTC(startDate, offset);
                const reqEndIso = formatToCameraUTC(endDate, offset);
                const res = await axios.get(`/api/analytics/?camera_id=${camera.id}&start_datetime=${reqStartIso}&end_datetime=${reqEndIso}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setAnalytics(res.data);

                if (onAnalyticsLoaded) {
                    onAnalyticsLoaded(camera.id, camera.operating_mode, res.data, camera.name);
                }
            } catch (e) {
                console.error("Ошибка загрузки аналитики", e);
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
    }, [camera, startDate, endDate, onAnalyticsLoaded]);

    // Мемоизация отдельных блоков данных
    const generalData = useMemo(() => {
        if (!analytics || !analytics.general) return null;
        return { ...analytics.general, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const chairData = useMemo(() => {
        if (!analytics || !analytics.chair) return null;
        return { ...analytics.chair, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const phoneData = useMemo(() => {
        if (!analytics || !analytics.phone) return null;
        return { ...analytics.phone, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const workFlowersData = useMemo(() => {
        if (!analytics || !analytics.work_flowers) return null;
        return { ...analytics.work_flowers, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const noUniformData = useMemo(() => {
        if (!analytics || !analytics.no_uniform) return null;
        return { ...analytics.no_uniform, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const fridgeData = useMemo(() => {
        if (!analytics || !analytics.fridge_empty) return null;
        return { ...analytics.fridge_empty, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const unattendedData = useMemo(() => {
        if (!analytics || !analytics.unattended_clients) return null;
        return { ...analytics.unattended_clients, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const receiptData = useMemo(() => {
        if (!analytics || !analytics.receipts) return null;
        return { ...analytics.receipts, start_date: analytics.date_range.start, end_date: analytics.date_range.end };
    }, [analytics]);

    const activeTimelineData = useMemo(() => {
        if (!analytics) return [];
        if (activeView === "general") return analytics.general?.timeline || [];
        if (activeView === "chair") return analytics.chair?.timeline || [];
        if (activeView === "phone") return analytics.phone?.timeline || [];
        if (activeView === "work_flowers") return analytics.work_flowers?.timeline || [];
        if (activeView === "no_uniform") return analytics.no_uniform?.timeline || [];
        if (activeView === "fridge_empty") return analytics.fridge_empty?.timeline || [];
        if (activeView === "unattended_clients") return analytics.unattended_clients?.timeline || [];
        if (activeView === "receipts") return analytics.receipts?.timeline || [];
        return [];
    }, [analytics, activeView]);

    const activeMeta = useMemo(() => {
        if (activeView === "chair") return chairMeta;
        if (activeView === "phone") return phoneMeta;
        if (activeView === "work_flowers") return workFlowersMeta;
        if (activeView === "no_uniform") return noUniformMeta;
        if (activeView === "fridge_empty") return fridgeMeta;
        if (activeView === "unattended_clients") return unattendedClientMeta;
        if (activeView === "receipts") return receiptMeta;
        return personnelGeneralMeta;
    }, [activeView, chairMeta, phoneMeta, workFlowersMeta, noUniformMeta, fridgeMeta, unattendedClientMeta, receiptMeta, personnelGeneralMeta]);

    const handleSegmentDoubleClick = async (itemId, exactTimeIso) => {
        let targetTimeIso = exactTimeIso;
        if (!targetTimeIso && itemId !== null && activeTimelineData && activeTimelineData[itemId]) {
            targetTimeIso = activeTimelineData[itemId].start;
        }

        if (targetTimeIso) {
            onOpenMonitor(monitorCameras, targetTimeIso);
        }
    };

    const receiptEvents = useMemo(() => {
        if (!analytics || !analytics.receipts || !analytics.receipts.timeline) return [];

        const activeSegments = analytics.receipts.timeline.filter(
            evt => evt.class_id !== -110 && evt.class_id !== -1
        );

        if (activeSegments.length === 0) return [];

        const sorted = [...activeSegments].sort((a, b) => new Date(a.start) - new Date(b.start));
        const merged = [];
        let current = { ...sorted[0] };

        for (let i = 1; i < sorted.length; i++) {
            const next = sorted[i];
            const currentEndMs = new Date(current.end).getTime();
            const nextStartMs = new Date(next.start).getTime();
            const gapMs = nextStartMs - currentEndMs;

            if (gapMs < 240000) {
                if (new Date(next.end).getTime() > currentEndMs) {
                    current.end = next.end;
                }
            } else {
                merged.push(current);
                current = { ...next };
            }
        }
        merged.push(current);
        return merged;
    }, [analytics]);

    const isFridge = camera.operating_mode === "fridge";
    const isReceipt = camera.operating_mode === "receipt";
    const isVisitorCounting = camera.operating_mode === 'visitor_counting';
    const hintStyle = { borderBottom: '1px dashed #999', cursor: 'help', width: 'fit-content' };

    return (
        <CCard className="mb-4 shadow-sm border-0">
            <CCardHeader className="bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center">
                    <CIcon icon={cilVideo} className="me-2 text-primary" size="lg" />
                    <h5 className="m-0 fw-bold">{camera.name}</h5>
                    <CBadge color="secondary" className="ms-3">{camera.operating_mode}</CBadge>
                    {sourceIds.length > 1 && (
                        <CBadge color="info" className="ms-2">Совмещенный ({sourceIds.length} ракурса)</CBadge>
                    )}
                    <div className="ms-4 text-muted small border-start ps-3 d-none d-md-block">
                        График: <strong>
                            {camera.has_multiple_shifts
                                ? "Несколько смен"
                                : `${camera.work_start_time?.substring(0, 5)} - ${camera.work_end_time?.substring(0, 5)}`}
                        </strong>
                    </div>
                </div>

                <CButton
                    color="primary"
                    variant="outline"
                    size="sm"
                    href={`/camera/${camera.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Настройки поста <CIcon icon={cilExternalLink} className="ms-1" size="sm" />
                </CButton>
            </CCardHeader>
            <CCardBody>
                <CRow>
                    {/* ЛЕВАЯ ЧАСТЬ: Снапшоты с отмасштабированным ROI */}
                    <CCol xl={4} className="mb-3">
                        {sourceIds.map((id, index) => (
                            <div key={id} className={index > 0 ? "mt-3" : ""}>
                                <RoiEditor
                                    snapshotUrl={snapshots[id]}
                                    polygonPoints={polygons[id] || { current: [] }}
                                    setPolygonPoints={() => { }}
                                    isLive={false}
                                    showRoi={globalShowRoi}
                                    onSave={() => { }}
                                    onReset={() => { }}
                                    onClear={() => { }}
                                    cameraName={sourceIds.length > 1 ? sourceCameraNames[id] : null}
                                    onImageLoad={(dim) => handleImageLoaded(id, dim)}
                                    readOnly={true}
                                    operatingMode={camera.operating_mode}
                                />
                            </div>
                        ))}
                    </CCol>

                    {/* ПРАВАЯ ЧАСТЬ: Интерактивная аналитика */}
                    <CCol xl={8}>
                        {loading ? (
                            <div className="text-center p-5"><CSpinner /></div>
                        ) : analytics ? (
                            <>
                                {/* 1. РЕЖИМ ПОДСЧЕТА ПОСЕТИТЕЛЕЙ */}
                                {isVisitorCounting && (
                                    <VisitorAnalyticsBlock
                                        analytics={analytics}
                                        onSegmentDoubleClick={handleSegmentDoubleClick}
                                    />
                                )}

                                {/* 2. ВСЕ ОСТАЛЬНЫЕ РЕЖИМЫ (Персонал, Холодильник, Чек) */}
                                {!isVisitorCounting && (
                                    <>
                                        {/* ОБЩАЯ СВОДКА (ДИНАМИЧЕСКИЙ БЛОК МЕТРИК) */}
                                        {dynamicSummary && (
                                            <CCard className="mb-4 bg-light shadow-sm border-secondary border-top-5">
                                                <CCardHeader className="fw-bold text-uppercase">{t("camera.dynamic_summary_title")}</CCardHeader>
                                                <CCardBody>
                                                    <CRow>
                                                        <CCol md={6}>
                                                            <CListGroup flush className="bg-transparent">
                                                                <CListGroupItem className="bg-transparent ps-0">
                                                                    <CTooltip content={isFridge ? "Максимальное количество одновременно пустующих мест" : isReceipt ? "Максимальное количество чеков в кадре" : t("tooltips.max_people")}>
                                                                        <span className="text-muted small d-block text-uppercase" style={hintStyle}>
                                                                            {isFridge ? "Макс. пустых мест" : isReceipt ? "Макс. чеков/касс" : t("camera.max_people_working")}
                                                                        </span>
                                                                    </CTooltip>
                                                                    <span className="fs-5 fw-bold">{dynamicSummary.maxPeople} {isFridge ? "мест" : isReceipt ? "шт." : t("camera.people_unit")}</span>
                                                                </CListGroupItem>

                                                                <CListGroupItem className="bg-transparent ps-0">
                                                                    <CTooltip content={isFridge ? "Время, в течение которого пустых мест не было (все заполнено)" : isReceipt ? "Время, когда чеков в кадре не было обнаружено" : t("tooltips.downtime")}>
                                                                        <span className="text-muted small d-block text-uppercase" style={hintStyle}>
                                                                            {isFridge ? "Время: Всё заполнено" : isReceipt ? "Время: Нет чека в кадре" : t("camera.employee_absence")}
                                                                        </span>
                                                                    </CTooltip>
                                                                    <span className="fs-5 fw-bold">{formatDuration(dynamicSummary.totalDowntime)}</span>
                                                                </CListGroupItem>

                                                                {analytics && analytics.daily_presence && analytics.daily_presence.length > 0 && !isFridge && !isReceipt && (
                                                                    <div className="mt-3">
                                                                        <small className="fw-bold d-block mb-1">Появление сотрудников:</small>
                                                                        <div className="table-responsive">
                                                                            <table className="table table-sm table-bordered text-center align-middle mb-0 bg-white">
                                                                                <thead className="table-light">
                                                                                    <tr>
                                                                                        <th>Дата</th>
                                                                                        <th className="text-success">Первое</th>
                                                                                        <th className="text-danger">Последнее</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {analytics.daily_presence.map((row) => (
                                                                                        <tr key={row.date}>
                                                                                            <td>{dayjs(row.date).format("DD.MM")}</td>
                                                                                            <td className="text-success fw-bold">{row.first_appearance}</td>
                                                                                            <td className="text-danger fw-bold">{row.last_disappearance}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </CListGroup>
                                                        </CCol>

                                                        <CCol md={6}>
                                                            <CListGroup flush className="bg-transparent">
                                                                {isFridge ? (
                                                                    <>
                                                                        <CListGroupItem className="bg-transparent ps-0">
                                                                            <CTooltip content="Общее накопленное время, когда в холодильнике пустовали места">
                                                                                <span className="text-muted small d-block text-uppercase" style={hintStyle}>Общее время пустых мест</span>
                                                                            </CTooltip>
                                                                            <span className="fs-5 fw-bold text-info">{formatDuration(dynamicSummary.totalFridgeEmpty)}</span>
                                                                        </CListGroupItem>

                                                                        <CListGroupItem className="bg-transparent ps-0">
                                                                            <CTooltip content="Суммарное время, когда клиенты находились в холодильнике без сотрудников более 1 минуты">
                                                                                <span className="text-muted small d-block text-uppercase" style={hintStyle}>Клиенты без внимания (&gt;1 мин)</span>
                                                                            </CTooltip>
                                                                            <span className="fs-5 fw-bold text-warning">{formatDuration(dynamicSummary.totalUnattended)}</span>
                                                                            <small className="d-block text-muted">
                                                                                Инцидентов: <strong className="text-danger">{dynamicSummary.unattendedIncidents?.length || 0} шт.</strong>
                                                                            </small>
                                                                        </CListGroupItem>
                                                                    </>
                                                                ) : isReceipt ? (
                                                                    <CListGroupItem className="bg-transparent ps-0">
                                                                        <CTooltip content="Общее время фиксации чека или кассового аппарата в кадре">
                                                                            <span className="text-muted small d-block text-uppercase" style={hintStyle}>Время присутствия чека/кассы</span>
                                                                        </CTooltip>
                                                                        <span className="fs-5 fw-bold text-danger">{formatDuration(dynamicSummary.totalReceipts)}</span>
                                                                    </CListGroupItem>
                                                                ) : (
                                                                    <>
                                                                        <CListGroupItem className="bg-transparent ps-0">
                                                                            <CTooltip content={t("tooltips.total_work")}>
                                                                                <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.total_work_man_hours")}</span>
                                                                            </CTooltip>
                                                                            <span className="fs-5 fw-bold text-success">{formatDuration(dynamicSummary.totalWork)}</span>
                                                                        </CListGroupItem>

                                                                        <CListGroupItem className="bg-transparent ps-0">
                                                                            <CTooltip content={t("tooltips.chair")}>
                                                                                <span className="text-muted small d-block text-uppercase" style={hintStyle}>{t("camera.loss_chair")}</span>
                                                                            </CTooltip>
                                                                            <span className="fs-5 fw-bold text-warning">{formatDuration(dynamicSummary.totalChair)}</span>
                                                                        </CListGroupItem>
                                                                    </>
                                                                )}
                                                            </CListGroup>
                                                        </CCol>
                                                    </CRow>
                                                </CCardBody>
                                            </CCard>
                                        )}

                                        {/* ПЕРЕКЛЮЧАТЕЛЬ ВКЛАДОК ДЛЯ ХОЛОДИЛЬНИКА */}
                                        {isFridge && (
                                            <div className="mb-3 p-2 bg-light border rounded d-flex gap-3 align-items-center flex-wrap">
                                                <span className="text-muted small text-uppercase fw-bold me-2"><CIcon icon={cilOptions} /> Режим аналитики холодильника:</span>
                                                <CFormCheck type="radio" id={`fridge_tab1_${camera.id}`} name={`viewToggle_${camera.id}`} label="🧊 Пустые места в холодильнике" checked={activeView === "fridge_empty"} onChange={() => setActiveView("fridge_empty")} />
                                                <CFormCheck type="radio" id={`fridge_tab2_${camera.id}`} name={`viewToggle_${camera.id}`} label="⏳ Клиенты без внимания (>1 мин)" checked={activeView === "unattended_clients"} onChange={() => setActiveView("unattended_clients")} />
                                            </div>
                                        )}

                                        {/* ПЕРЕКЛЮЧАТЕЛЬ ВКЛАДОК ДЛЯ ПЕРСОНАЛА */}
                                        {!isFridge && !isReceipt && (
                                            <div className="mb-3 p-2 bg-light border rounded d-flex gap-3 align-items-center flex-wrap">
                                                <span className="text-muted small text-uppercase fw-bold me-2"><CIcon icon={cilOptions} /> {t("camera.view_toggle_label")}</span>
                                                <CFormCheck type="radio" id={`staff_tab1_${camera.id}`} name={`viewToggle_${camera.id}`} label={t("camera.view_general")} checked={activeView === "general"} onChange={() => setActiveView("general")} />
                                                <CFormCheck type="radio" id={`staff_tab2_${camera.id}`} name={`viewToggle_${camera.id}`} label={t("camera.view_chair")} checked={activeView === "chair"} onChange={() => setActiveView("chair")} />
                                                <CFormCheck type="radio" id={`staff_tab3_${camera.id}`} name={`viewToggle_${camera.id}`} label={t("camera.view_phone")} checked={activeView === "phone"} onChange={() => setActiveView("phone")} />
                                                <CFormCheck type="radio" id={`staff_tab4_${camera.id}`} name={`viewToggle_${camera.id}`} label="Работа с цветами" checked={activeView === "work_flowers"} onChange={() => setActiveView("work_flowers")} />
                                                <CFormCheck type="radio" id={`staff_tab5_${camera.id}`} name={`viewToggle_${camera.id}`} label="Без формы" checked={activeView === "no_uniform"} onChange={() => setActiveView("no_uniform")} />
                                            </div>
                                        )}

                                        {/* ГРАФИКИ И ТАЙМЛАЙНЫ ДЛЯ ПЕРСОНАЛА */}
                                        {!isFridge && !isReceipt && activeView === "general" && generalData && (
                                            <AnalyticsBlock title="Общая активность" analyticsData={generalData} classIdToMeta={personnelGeneralMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="general" operatingMode="personnel_control" />
                                        )}
                                        {!isFridge && !isReceipt && activeView === "chair" && chairData && (
                                            <AnalyticsBlock title="Сидя на стуле" analyticsData={chairData} classIdToMeta={chairMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="chair" operatingMode="personnel_control" />
                                        )}
                                        {!isFridge && !isReceipt && activeView === "phone" && phoneData && (
                                            <AnalyticsBlock title="В телефоне" analyticsData={phoneData} classIdToMeta={phoneMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="phone" operatingMode="personnel_control" />
                                        )}
                                        {!isFridge && !isReceipt && activeView === "work_flowers" && workFlowersData && (
                                            <AnalyticsBlock title="Работа с цветами" analyticsData={workFlowersData} classIdToMeta={workFlowersMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="work_flowers" operatingMode="personnel_control" />
                                        )}
                                        {!isFridge && !isReceipt && activeView === "no_uniform" && noUniformData && (
                                            <AnalyticsBlock title="Без формы" analyticsData={noUniformData} classIdToMeta={noUniformMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="no_uniform" operatingMode="personnel_control" />
                                        )}

                                        {/* ГРАФИКИ ДЛЯ ХОЛОДИЛЬНИКА */}
                                        {isFridge && activeView === "fridge_empty" && fridgeData && (
                                            <AnalyticsBlock title="Пустые места в холодильнике" analyticsData={fridgeData} classIdToMeta={fridgeMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="fridge_empty" operatingMode="fridge" />
                                        )}
                                        {isFridge && activeView === "unattended_clients" && unattendedData && (
                                            <AnalyticsBlock title="Клиенты в холодильнике без сотрудников (>1 мин)" analyticsData={unattendedData} classIdToMeta={unattendedClientMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="unattended_clients" operatingMode="fridge" />
                                        )}

                                        {/* ГРАФИК ДЛЯ ЧЕКОВ */}
                                        {isReceipt && activeView === "receipts" && receiptData && (
                                            <AnalyticsBlock title="Контроль наличия чека/кассы" analyticsData={receiptData} classIdToMeta={receiptMeta} onSegmentDoubleClick={handleSegmentDoubleClick} type="receipts" operatingMode="receipt" />
                                        )}



                                        {/* СПИСОК ЧЕКОВ */}
                                        {isReceipt && receiptEvents.length > 0 && (
                                            <CCard className="mb-4 shadow-sm border-top-danger border-top-3">
                                                <CCardHeader className="bg-white py-2 d-flex justify-content-between align-items-center">
                                                    <strong className="text-danger">История выдачи чеков ({receiptEvents.length} шт.)</strong>
                                                </CCardHeader>
                                                <CCardBody style={{ maxHeight: "250px", overflowY: "auto" }} className="p-0">
                                                    <div className="list-group list-group-flush">
                                                        {receiptEvents.map((evt, idx) => (
                                                            <button
                                                                key={idx}
                                                                type="button"
                                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3 small"
                                                                onClick={() => handleSegmentDoubleClick(null, evt.start)}
                                                            >
                                                                <div>
                                                                    <span className="badge bg-danger me-2">Чек</span>
                                                                    <strong>{dayjs(evt.start).format("HH:mm:ss")} — {dayjs(evt.end).format("HH:mm:ss")}</strong>
                                                                </div>
                                                                <span className="badge bg-secondary rounded-pill">
                                                                    {Math.round((new Date(evt.end) - new Date(evt.start)) / 1000)} сек.
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </CCardBody>
                                            </CCard>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <p className="text-muted text-center p-5">Данные не найдены</p>
                        )}
                    </CCol>
                </CRow>
            </CCardBody>
        </CCard>
    );
};

// ============================================================================
// КОМПОНЕНТ: СВЕРКА ВЫНОСА БУКЕТОВ С ЧЕКАМИ (ДИСПЕТЧЕР РАСХОЖДЕНИЙ)
// ============================================================================
const StoreDiscrepancyWidget = ({ storeAnalytics, onOpenMonitor, allStoreCameras }) => {
    const MATCH_WINDOW_SEC = 180;

    const { bouquetExits, receiptEvents } = useMemo(() => {
        let exits = [];
        let receipts = [];

        Object.values(storeAnalytics).forEach(camData => {
            if (camData.mode === 'visitor_counting' && camData.data?.raw_logs) {
                camData.data.raw_logs.forEach(log => {
                    if (log.direction === 'out' && log.has_bouquet) {
                        exits.push({ ...log, camera_name: camData.cameraName });
                    }
                });
            }

            if (camData.mode === 'receipt' && camData.data?.receipts?.timeline) {
                camData.data.receipts.timeline.forEach(seg => {
                    if (seg.class_id !== -110 && seg.class_id !== -1) {
                        receipts.push(seg);
                    }
                });
            }
        });

        exits.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        return { bouquetExits: exits, receiptEvents: receipts };
    }, [storeAnalytics]);

    const { discrepancies, confirmedExits } = useMemo(() => {
        const disc = [];
        const conf = [];

        bouquetExits.forEach(exit => {
            const exitTimeMs = new Date(exit.timestamp).getTime();

            const hasMatchingReceipt = receiptEvents.some(r => {
                const rStartMs = new Date(r.start).getTime();
                const rEndMs = new Date(r.end).getTime();
                const diffSec = Math.abs(exitTimeMs - rStartMs) / 1000;
                return diffSec <= MATCH_WINDOW_SEC || (exitTimeMs >= rStartMs && exitTimeMs <= rEndMs);
            });

            if (hasMatchingReceipt) conf.push(exit);
            else disc.push(exit);
        });

        return { discrepancies: disc, confirmedExits: conf };
    }, [bouquetExits, receiptEvents]);

    const totalBouquets = bouquetExits.length;
    const discrepancyRate = totalBouquets > 0 ? ((discrepancies.length / totalBouquets) * 100).toFixed(1) : 0;

    return (
        <CCard className="mb-4 shadow-sm border-top-danger border-top-3">
            <CCardHeader className="bg-white py-3 d-flex justify-content-between align-items-center flex-wrap">
                <div className="d-flex align-items-center gap-2">
                    <CIcon icon={cilWarning} className="text-danger" size="xl" />
                    <h4 className="m-0 fw-bold text-dark">Контроль выноса товара (Сверка выходов и чеков)</h4>
                </div>
                <CBadge color={discrepancies.length > 0 ? "danger" : "success"} className="p-2 fs-6">
                    {discrepancies.length > 0 ? `Подозрительных выносов: ${discrepancies.length}` : "Все выносы подтверждены чеками"}
                </CBadge>
            </CCardHeader>

            <CCardBody>
                <CRow className="mb-4 text-center">
                    <CCol md={3}>
                        <div className="p-3 bg-light rounded border">
                            <small className="text-muted text-uppercase fw-bold d-block mb-1">Вынесено букетов</small>
                            <span className="fs-3 fw-bold text-dark">{totalBouquets} шт.</span>
                        </div>
                    </CCol>
                    <CCol md={3}>
                        <div className="p-3 bg-light rounded border">
                            <small className="text-muted text-uppercase fw-bold d-block mb-1">Подтверждено чеком (±3 мин)</small>
                            <span className="fs-3 fw-bold text-success">{confirmedExits.length} шт.</span>
                        </div>
                    </CCol>
                    <CCol md={3}>
                        <div className="p-3 bg-light rounded border">
                            <small className="text-muted text-uppercase fw-bold d-block mb-1">Вынос БЕЗ ЧЕКА</small>
                            <span className="fs-3 fw-bold text-danger">{discrepancies.length} шт.</span>
                        </div>
                    </CCol>
                    <CCol md={3}>
                        <div className="p-3 bg-light rounded border">
                            <small className="text-muted text-uppercase fw-bold d-block mb-1">% Риска</small>
                            <span className={`fs-3 fw-bold ${discrepancyRate > 15 ? "text-danger" : "text-warning"}`}>
                                {discrepancyRate}%
                            </span>
                        </div>
                    </CCol>
                </CRow>

                {discrepancies.length > 0 && (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <CTable striped hover responsive align="middle" className="text-center mb-0 border">
                            <CTableHead color="light" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                <CTableRow>
                                    <CTableHeaderCell>Время выхода</CTableHeaderCell>
                                    <CTableHeaderCell>Камера выхода</CTableHeaderCell>
                                    <CTableHeaderCell>Пол и Возраст</CTableHeaderCell>
                                    <CTableHeaderCell>Статус чека</CTableHeaderCell>
                                    <CTableHeaderCell>Действие</CTableHeaderCell>
                                </CTableRow>
                            </CTableHead>
                            <CTableBody>
                                {discrepancies.map((log) => (
                                    <CTableRow key={log.id}>
                                        <CTableDataCell className="fw-bold text-danger">
                                            {dayjs(log.timestamp).format('DD.MM.YYYY HH:mm:ss')}
                                        </CTableDataCell>
                                        <CTableDataCell>{log.camera_name}</CTableDataCell>
                                        <CTableDataCell>
                                            {log.gender === 'm' ? 'Мужчина' : 'Женщина'} (Группа {log.age_group})
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <CBadge color="danger" className="p-2">
                                                ЧЕК НЕ НАЙДЕН (±3 мин)
                                            </CBadge>
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <CButton
                                                color="primary"
                                                size="sm"
                                                onClick={() => onOpenMonitor(allStoreCameras, log.timestamp)}
                                            >
                                                <CIcon icon={cilMonitor} className="me-1" /> Проверить
                                            </CButton>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))}
                            </CTableBody>
                        </CTable>
                    </div>
                )}
            </CCardBody>
        </CCard>
    );
};

// ============================================================================
// ГЛАВНЫЙ КОМПОНЕНТ СТРАНИЦЫ МАГАЗИНА
// ============================================================================
const StoreDetailPage = () => {
    const { t, i18n } = useTranslation();
    const { storeId } = useParams();
    const location = useLocation();

    const [store, setStore] = useState(null);
    const [loading, setLoading] = useState(true);

    const [startDate, setStartDate] = useState(dayjs().startOf('day').toDate());
    const [endDate, setEndDate] = useState(dayjs().endOf('day').toDate());

    const [globalShowRoi, setGlobalShowRoi] = useState(true);
    const [storeAnalytics, setStoreAnalytics] = useState({});

    const [monitorConfig, setMonitorConfig] = useState({
        visible: false,
        mode: 'live',
        initialIsoTime: null,
        cameras: []
    });

    const [audioSegments, setAudioSegments] = useState([]);
    const [audioSuggestions, setAudioSuggestions] = useState([]);
    const [selectedAudioSegment, setSelectedAudioSegment] = useState(null);
    const [audioModalVisible, setAudioModalVisible] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [audioCameraNames, setAudioCameraNames] = useState([]);

    useEffect(() => {
        const fetchStore = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`/api/stores/${storeId}/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStore(res.data);
            } catch (e) {
                toast.error('Не удалось загрузить данные магазина');
            } finally {
                setLoading(false);
            }
        };
        fetchStore();
    }, [storeId]);

    const handleCameraAnalyticsLoaded = useCallback((camId, mode, data, cameraName) => {
        setStoreAnalytics(prev => ({
            ...prev,
            [camId]: { mode, data, cameraName }
        }));
    }, []);

    const fetchGlobalAudio = useCallback(async () => {
        if (!store) return;
        const audioCams = store.cameras.filter(c => c.record_audio);
        if (audioCams.length === 0) return;

        setAudioCameraNames(audioCams.map(c => c.name));
        setLoadingAudio(true);
        try {
            const token = localStorage.getItem("token");
            let allSegments = [];
            let allSuggestions = new Set();

            for (const cam of audioCams) {
                const offset = cam.timezone_offset || 0;
                const reqStartIso = formatToCameraUTC(startDate, offset);
                const reqEndIso = formatToCameraUTC(endDate, offset);

                const res = await axios.get(`/api/audio-analytics/?camera_id=${cam.id}&start_datetime=${reqStartIso}&end_datetime=${reqEndIso}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data.segments) {
                    const segmentsWithCam = res.data.segments.map(s => ({ ...s, camera_name: cam.name }));
                    allSegments = [...allSegments, ...segmentsWithCam];
                }
                if (res.data.suggestions) {
                    res.data.suggestions.forEach(s => allSuggestions.add(s));
                }
            }

            allSegments.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            setAudioSegments(allSegments);
            setAudioSuggestions(Array.from(allSuggestions).sort());
        } catch (e) {
            console.error("Ошибка загрузки глобального аудио", e);
        } finally {
            setLoadingAudio(false);
        }
    }, [store, startDate, endDate]);

    useEffect(() => {
        if (store) fetchGlobalAudio();
    }, [store, startDate, endDate, fetchGlobalAudio]);

    const handleOpenMonitor = (camerasToWatch, timeIso = null) => {
        setMonitorConfig({
            visible: true,
            mode: timeIso ? 'archive' : 'live',
            initialIsoTime: timeIso,
            cameras: camerasToWatch
        });
    };

    useEffect(() => {
        if (store && !loading) {
            const queryParams = new URLSearchParams(location.search);
            const timeIso = queryParams.get('time');
            const audioAlertId = queryParams.get('audio_alert');

            if (timeIso) {
                const allStoreSourceCameras = flattenCameras(store.cameras);
                handleOpenMonitor(allStoreSourceCameras, timeIso);
            } else if (audioAlertId) {
                const token = localStorage.getItem('token');
                axios.get(`/api/audio-alerts/${audioAlertId}/`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).then(res => {
                    const alt = res.data;
                    const dummySegment = {
                        id: alt.id,
                        start_time: alt.timestamp,
                        start_sec: alt.start_sec,
                        audio_url: alt.audio_url,
                        context_type: alt.segment_data?.context_type || 'Неизвестно',
                        dialog_markers: alt.segment_data?.dialog_markers || {},
                        topics: alt.segment_data?.topics || {},
                        raw_lines: alt.raw_lines || []
                    };
                    setSelectedAudioSegment(dummySegment);
                    setAudioModalVisible(true);
                }).catch(e => console.error("Ошибка загрузки аудио-инцидента", e));
            }
        }
    }, [store, loading, location.search]);

    const scriptData = useMemo(() => {
        let totalScores = [];
        let stepsStats = {
            'Приветствие': { passed: 0, total: 0 },
            'Потребности': { passed: 0, total: 0 },
            'Презентация': { passed: 0, total: 0 },
            'Допродажи/Касса': { passed: 0, total: 0 },
            'Прощание': { passed: 0, total: 0 },
        };

        audioSegments.forEach(seg => {
            const compliance = seg.script_compliance;
            if (compliance && compliance.total_score !== undefined) {
                totalScores.push(compliance.total_score);
                if (Array.isArray(compliance.checklist)) {
                    compliance.checklist.forEach(item => {
                        const name = (item.step_name || '').toLowerCase();
                        let key = null;
                        if (name.includes('привет')) key = 'Приветствие';
                        else if (name.includes('потреб')) key = 'Потребности';
                        else if (name.includes('презент')) key = 'Презентация';
                        else if (name.includes('касс') || name.includes('допрод')) key = 'Допродажи/Касса';
                        else if (name.includes('прощ')) key = 'Прощание';

                        if (key) {
                            stepsStats[key].total += 1;
                            if (item.passed) stepsStats[key].passed += 1;
                        }
                    });
                }
            }
        });

        const avgScore = totalScores.length > 0 ? (totalScores.reduce((a, b) => a + b, 0) / totalScores.length).toFixed(1) : 0;

        const barData = Object.keys(stepsStats).map(key => ({
            step: key,
            "Выполнено %": stepsStats[key].total > 0 ? Math.round((stepsStats[key].passed / stepsStats[key].total) * 100) : 0
        }));

        return { avgScore, barData, count: totalScores.length };
    }, [audioSegments]);

    if (loading) return <Loading name="Сборка дашборда магазина..." />;
    if (!store) return <div className="p-4 text-center">Магазин не найден</div>;

    const hasAudio = store.cameras.some(c => c.record_audio);
    const allStoreSourceCameras = flattenCameras(store.cameras);

    return (
        <div className="bg-light min-vh-100 p-4">
            <CContainer fluid>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <h2 className="m-0"><CIcon icon={cilHome} className="me-2 text-primary" />{store.name}</h2>
                        <CBadge color="primary" className="p-2 fs-6">{store.cameras.length} постов</CBadge>
                    </div>
                    <div className="d-flex gap-2">
                        <CButton color="dark" onClick={() => handleOpenMonitor(allStoreSourceCameras)}>
                            <CIcon icon={cilMonitor} className="me-2" /> Мульти-монитор магазина
                        </CButton>
                        <CButton color="secondary" href="/dashboard">
                            <CIcon icon={cilArrowLeft} className="me-2" /> Назад
                        </CButton>
                        <LanguageSwitcher />
                    </div>
                </div>

                <CCard className="mb-4 shadow-sm border-top-info border-top-3">
                    <CCardBody className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-2 text-info">
                            <CIcon icon={cilLockLocked} size="xl" />
                            <div>
                                <strong>Режим сводного дашборда:</strong> Изменение настроек, смен и зон ROI заблокировано.
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-3 bg-white p-2 rounded border">
                            <div className="d-flex align-items-center gap-2">
                                <label className="mb-0 fw-bold">{t("common.date_start")}:</label>
                                <DatePicker selected={startDate} onChange={setStartDate} showTimeSelect dateFormat="dd.MM.yyyy HH:mm" className="form-control form-control-sm" locale={i18n.language === 'ru' ? 'ru' : 'en'} />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label className="mb-0 fw-bold">{t("common.date_end")}:</label>
                                <DatePicker selected={endDate} onChange={setEndDate} showTimeSelect dateFormat="dd.MM.yyyy HH:mm" className="form-control form-control-sm" locale={i18n.language === 'ru' ? 'ru' : 'en'} />
                            </div>
                            <div className="ms-3 border-start ps-3">
                                <CFormCheck id="globalRoiCheck" label="Отображать зоны ROI на фото" checked={globalShowRoi} onChange={(e) => setGlobalShowRoi(e.target.checked)} />
                            </div>
                        </div>
                    </CCardBody>
                </CCard>

                <StoreAlertsWidget
                    storeCameras={store.cameras.map(c => c.id)}
                    onOpenAudioModal={(seg) => {
                        setSelectedAudioSegment(seg);
                        setAudioModalVisible(true);
                    }}
                />

                <StoreDiscrepancyWidget
                    storeAnalytics={storeAnalytics}
                    onOpenMonitor={handleOpenMonitor}
                    allStoreCameras={allStoreSourceCameras}
                />

                <StoreShiftAnalyticsWidget
                    store={store}
                    storeAnalytics={storeAnalytics}
                    onStoreUpdate={(updatedStore) => setStore(updatedStore)}
                />

                {scriptData.count > 0 && (
                    <CCard className="mb-4 shadow-sm border-top-success border-top-3">
                        <CCardHeader className="bg-white py-3">
                            <strong className="fs-5 text-success">
                                <CIcon icon={cilSpeech} className="me-2" />
                                Соблюдение скрипта продаж: {scriptData.avgScore}% (на основе {scriptData.count} диалогов)
                            </strong>
                        </CCardHeader>
                        <CCardBody style={{ height: "300px" }}>
                            <ResponsiveBar
                                data={scriptData.barData}
                                keys={['Выполнено %']}
                                indexBy="step"
                                margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                                padding={0.3}
                                colors={["#2eb85c"]}
                                axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
                                axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: '% выполнения', legendPosition: 'middle', legendOffset: -45 }}
                                labelSkipWidth={12}
                                labelSkipHeight={12}
                                labelTextColor="#ffffff"
                            />
                        </CCardBody>
                    </CCard>
                )}

                <div className="mb-5">
                    {store.cameras.map(camera => (
                        <StoreCameraCard
                            key={camera.id}
                            camera={camera}
                            startDate={startDate}
                            endDate={endDate}
                            globalShowRoi={globalShowRoi}
                            onOpenMonitor={handleOpenMonitor}
                            onAnalyticsLoaded={handleCameraAnalyticsLoaded}
                        />
                    ))}
                </div>

                {hasAudio && (
                    <div className="mt-5 border-top pt-4">
                        <h4 className="text-primary mb-3"><CIcon icon={cilSpeech} className="me-2" /> Сводный контроль диалогов (Аудио)</h4>
                        {loadingAudio ? (
                            <div className="text-center p-4"><CSpinner /> Загрузка аудиоданных магазина...</div>
                        ) : (
                            <AudioSearchDashboard
                                segments={audioSegments}
                                suggestions={audioSuggestions}
                                availableCameras={audioCameraNames}
                                onSegmentSelect={(seg) => {
                                    setSelectedAudioSegment(seg);
                                    setAudioModalVisible(true);
                                }}
                            />
                        )}
                        <AudioDetailModal
                            visible={audioModalVisible}
                            onClose={() => setAudioModalVisible(false)}
                            segment={selectedAudioSegment}
                            onWatchVideo={(startTimeIso) => {
                                handleOpenMonitor(allStoreSourceCameras, startTimeIso);
                            }}
                        />
                    </div>
                )}

                {monitorConfig.visible && (
                    <MultiCameraMonitor
                        cameras={monitorConfig.cameras}
                        onClose={() => setMonitorConfig(p => ({ ...p, visible: false }))}
                        initialConfig={monitorConfig}
                    />
                )}
            </CContainer>
        </div>
    );
};

export default StoreDetailPage;